import {
  IAccessReviewDisableResult,
  IAccessReviewRow,
  IUserResponse,
} from "types/User";

import { api } from ".";

const userQueries = api.injectEndpoints({
  endpoints: (builder) => ({
    getCurrentUser: builder.query<IUserResponse, null | void>({
      query: () => `user/current/overview`,
    }),
    createUser: builder.mutation({
      query: (newUser) => ({
        url: "user/create",
        method: "POST",
        body: newUser,
      }),
    }),
    resetPassword: builder.mutation({
      query: (userId) => ({
        method: "PUT",
        url: `user/resetPassword/${userId}`,
      }),
    }),
    deleteUser: builder.mutation({
      query: (userId) => ({
        method: "DELETE",
        url: `user/${userId}`,
      }),
    }),
    editUser: builder.mutation({
      query: ({ id, user }) => ({
        method: "PUT",
        body: user,
        url: `user/update/${id}`,
      }),
    }),
    getUserById: builder.query<
      { data: IUserResponse; message: string },
      string | undefined
    >({
      query: (id) => `user/${id}`,
    }),
    getAllUsers: builder.query<
      { data: IUserResponse[]; message: string },
      null | void
    >({
      query: () => `user`,
      providesTags: ["Users"],
    }),
    // CLE-2765: the Access review tab. Every access mutation invalidates both tags so the
    // Users tab (Disabled tag / Enable) and the review list refresh together.
    getAccessReview: builder.query<{ data: IAccessReviewRow[] }, null | void>({
      query: () => `user/access-review`,
      providesTags: ["AccessReview"],
    }),
    disableAccessReviewUsers: builder.mutation<
      { data: IAccessReviewDisableResult[] },
      string[]
    >({
      query: (userIds) => ({
        method: "POST",
        url: `user/access-review/disable`,
        body: { userIds },
      }),
      invalidatesTags: ["AccessReview", "Users"],
    }),
    keepAccessReviewUser: builder.mutation<{ data: IUserResponse }, string>({
      query: (userId) => ({
        method: "POST",
        url: `user/${userId}/access-review/keep`,
      }),
      invalidatesTags: ["AccessReview", "Users"],
    }),
    enableUser: builder.mutation<{ data: IUserResponse }, string>({
      query: (userId) => ({
        method: "POST",
        url: `user/${userId}/enable`,
      }),
      invalidatesTags: ["AccessReview", "Users"],
    }),
    disableUser: builder.mutation<{ data: IAccessReviewDisableResult }, string>(
      {
        query: (userId) => ({
          method: "POST",
          url: `user/${userId}/disable`,
        }),
        invalidatesTags: ["AccessReview", "Users"],
      }
    ),
    updateDefaultAccount: builder.mutation({
      query: ({ userId, defaultAccount }) => ({
        method: "PUT",
        url: `user/update-default-account/${userId}`,
        body: { defaultAccount },
      }),
    }),
    // CLE-2439: self-service profile edit (name only — email change is admin-only) and
    // password reset for the authenticated user. The backend derives the target from the
    // JWT, so no user id is sent.
    updateOwnProfile: builder.mutation<
      { data: IUserResponse; message: string },
      { name: string }
    >({
      query: (body) => ({
        method: "PUT",
        url: `user/self/profile`,
        body,
      }),
    }),
    requestOwnPasswordReset: builder.mutation<{ message: string }, void>({
      query: () => ({
        method: "POST",
        url: `user/self/password-reset`,
      }),
    }),
  }),
});

export const {
  useGetCurrentUserQuery,
  useGetAllUsersQuery,
  useCreateUserMutation,
  useGetUserByIdQuery,
  useEditUserMutation,
  useResetPasswordMutation,
  useDeleteUserMutation,
  useUpdateDefaultAccountMutation,
  useUpdateOwnProfileMutation,
  useRequestOwnPasswordResetMutation,
  useGetAccessReviewQuery,
  useDisableAccessReviewUsersMutation,
  useKeepAccessReviewUserMutation,
  useEnableUserMutation,
  useDisableUserMutation,
} = userQueries;
